import React from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import _get from 'lodash/get';

import { Link } from 'src/components/Inputs';
import Btn from 'src/components-v2/Inputs/Button/Btn';
import { ButtonTypes } from 'src/lib/constants';

import hyperlinkStyles from 'src/components/RichTextRenderer/Hyperlink/Hyperlink.module.scss';
import styles from './BlogPost.module.scss';

// eslint-disable-next-line react/prop-types
const HyperlinkButton = ({ href, trackingId, entryId, children }) => {
  const inspectorProps = useContentfulInspectorMode({ entryId });
  return (
    <Link href={href}>
      <a
        href={href}
        trackingId={trackingId}
        className={hyperlinkStyles.hyperlink}
        {...(entryId ? inspectorProps({ fieldId: 'text' }) : null)}
      >
        {children}
      </a>
    </Link>
  );
};

/**
 * Renders a blog rich-text CTA with the theme-aware v2 `Btn`.
 *
 * Passed to `RichTextRenderer` via its `renderEmbeddedButton` hook so the blog
 * gets the redesigned buttons while the shared legacy renderer (and the legacy
 * pages still using it) are left untouched.
 *
 * PRIMARY_CTA → primary, SECONDARY/SECONDARY_CTA → secondary, DEFAULT → tertiary.
 */
// eslint-disable-next-line react/prop-types
const BlogEmbeddedButton = ({ button, trackingId = '' }) => {
  const buttonType = _get(button, 'fields.type');
  const sys = _get(button, 'sys');
  const entryId = _get(button, 'sys.id');
  const btnProps = {
    href: _get(button, 'fields.url'),
    trackingId,
  };
  const buttonLabel = _get(button, 'fields.text');

  let rendered;
  switch (buttonType) {
    case ButtonTypes.HYPERLINK:
      rendered = (
        <HyperlinkButton {...btnProps} entryId={entryId}>
          {buttonLabel}
        </HyperlinkButton>
      );
      break;
    case ButtonTypes.PRIMARY_CTA:
      rendered = (
        <Btn {...btnProps} variant='primary' sys={sys}>
          {buttonLabel}
        </Btn>
      );
      break;
    case ButtonTypes.SECONDARY:
    case ButtonTypes.SECONDARY_CTA:
      rendered = (
        <Btn {...btnProps} variant='secondary' sys={sys}>
          {buttonLabel}
        </Btn>
      );
      break;
    case ButtonTypes.DEFAULT:
      rendered = (
        <Btn {...btnProps} variant='tertiary' sys={sys}>
          {buttonLabel}
        </Btn>
      );
      break;
    default:
      // Unknown type from CMS — log and skip rather than crashing the page.
      // eslint-disable-next-line no-console
      console.error(
        `Unknown button type: "${buttonType}" @ BlogEmbeddedButton`,
      );
      return null;
  }

  return <div className={styles.buttonWrapper}>{rendered}</div>;
};

export default BlogEmbeddedButton;
